/**
 * CORP//LLM — Admin Panel
 * Player management (beta toggle, online status, session details, delete),
 * bot account management (list, delete), and session overview.
 * Only accessible to users with admin JWT claim.
 */
import { getJwt, parseJwt } from '../auth';
import {
  getAdminPlayers, patchAdminPlayer, deleteAdminPlayer,
  getAdminSessions, getAdminOnline, getAdminPlayerSessions,
  resolveTickAsync, getTickJob, ApiError,
  createAdminSession, deleteAdminSession, spawnBotsAdmin,
  adminCreateNotification, adminBroadcastDiscord, getMotd, adminPutMotd,
} from '../api';
import type { AdminPlayer, AdminSession, NotificationTarget, NotificationSeverity } from '../api';
import { t } from '../i18n';

// resolveTickAndPoll wraps the #228 Phase-4 async tick endpoint with a
// simple HTTP polling loop. Returns the completed job or throws on
// failure / timeout. WS-progress integration is a follow-up; polling is
// sufficient for the admin button's "wait for tick to finish" UX.
async function resolveTickAndPoll(
  sessionId: string,
  onProgress?: (phase: string, pct: number) => void,
  pollIntervalMs = 1000,
  timeoutMs = 600_000,
): Promise<{ day: number; gameOver: boolean }> {
  const enq = await resolveTickAsync(sessionId);
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const job = await getTickJob(enq.jobId);
    if (onProgress) onProgress(job.progressPhase, job.progressPct);
    if (job.status === 'completed') {
      return { day: job.day, gameOver: false };
    }
    if (job.status === 'failed') {
      throw new Error(job.error || 'tick job failed');
    }
    await new Promise(r => setTimeout(r, pollIntervalMs));
  }
  throw new Error('tick job timed out');
}

export function renderAdmin(): HTMLElement {
  const root = document.createElement('div');
  root.className = 'screen';

  const jwt = getJwt();
  const payload = jwt ? parseJwt(jwt) : null;
  const isAdmin = payload?.admin === true;

  const style = document.createElement('style');
  style.textContent = `
    .adm-wrap { max-width: 960px; padding: 20px 0; }
    .adm-title { font-size: 1.1rem; color: var(--text-hi); letter-spacing: 0.15em; margin-bottom: 16px; }
    /* tabs/table/badge/toggle/delete/status/resolve → shared .tab/.dtable/.chip/.btn
       (marker class names kept for JS + tests); only the color/state variants stay local */
    .adm-badge.admin { border-color: var(--red); color: var(--red); }
    .adm-badge.bot { border-color: var(--violet); color: var(--violet); }
    .adm-toggle.on { border-color: var(--cyan); color: var(--cyan); background: rgba(var(--ac),0.08); }
    .adm-status.lobby { border-color: var(--cyan); color: var(--cyan); }
    .adm-status.running { border-color: var(--amber); color: var(--amber); }
    .adm-status.finished { border-color: var(--text-dim); color: var(--text-dim); }
    .adm-resolve-status { font-size: 0.68rem; color: var(--text-lo); margin-top: 2px; }
    .adm-loading { color: var(--text-dim); font-size: 0.85rem; padding: 20px 0; }
    .adm-no-access { color: var(--red, #e74c3c); font-size: 0.9rem; padding: 40px 0; text-align: center; }
    .adm-email { color: var(--text-dim); font-size: 0.75rem; }
    .adm-avatar { width: 28px; height: 28px; border-radius: 50%; vertical-align: middle; margin-right: 8px; object-fit: cover; }
    .adm-avatar-fallback { width: 28px; height: 28px; border-radius: 50%; display: inline-flex; align-items: center;
      justify-content: center; background: rgba(var(--ac),0.15); color: var(--cyan, #00f5d4); font-size: 0.7rem;
      font-weight: 600; vertical-align: middle; margin-right: 8px; }
    .adm-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; vertical-align: middle; }
    .adm-dot.online { background: var(--green); box-shadow: 0 0 4px rgba(46,204,113,0.5); }
    .adm-dot.offline { background: rgba(255,255,255,0.15); }
    .adm-online-session { font-size: 0.65rem; color: var(--text-dim); margin-left: 4px; }
    .adm-player-row { cursor: pointer; }
    .adm-player-row.expanded td { border-bottom-color: transparent; }
    .adm-chevron { display: inline-block; transition: transform 0.15s; font-size: 0.7rem; color: var(--text-dim);
      margin-right: 6px; }
    .adm-chevron.open { transform: rotate(90deg); }
    .adm-detail-row td { padding: 0 10px 10px 50px; }
    .adm-detail-session { display: flex; align-items: center; gap: 10px; padding: 4px 0; font-size: 0.78rem; }
    .adm-detail-color { width: 12px; height: 12px; border-radius: 2px; flex-shrink: 0; }
    .adm-detail-corp { color: var(--cyan, #00f5d4); font-weight: 500; }
    .adm-detail-name { color: var(--text-mid); }
    .adm-detail-empty { color: var(--text-dim); font-style: italic; font-size: 0.75rem; padding: 4px 0; }
    .adm-detail-loading { color: var(--text-dim); font-size: 0.75rem; padding: 4px 0; }
    .adm-login-time { font-family: var(--mono); font-size: 0.75rem; }
  `;
  root.appendChild(style);

  if (!isAdmin) {
    root.innerHTML += `<div class="adm-wrap"><div class="adm-no-access">${t('admin.no_access')}</div></div>`;
    return root;
  }

  const wrap = document.createElement('div');
  wrap.className = 'adm-wrap';
  wrap.innerHTML = `
    <div class="adm-title">${t('admin.title')}</div>
    <div class="adm-tabs tabs">
      <button class="adm-tab tab active" id="adm-tab-players">${t('admin.tab_players')}</button>
      <button class="adm-tab tab" id="adm-tab-bots">${t('admin.tab_bots')}</button>
      <button class="adm-tab tab" id="adm-tab-admin-sessions">${t('admin.tab_admin_sessions')}</button>
      <button class="adm-tab tab" id="adm-tab-sessions">${t('admin.tab_sessions')}</button>
      <button class="adm-tab tab" id="adm-tab-updates">${t('admin.tab_updates')}</button>
    </div>
    <div id="adm-view-players"><div class="adm-loading">${t('admin.loading')}</div></div>
    <div id="adm-view-bots" style="display:none"><div class="adm-loading">${t('admin.loading')}</div></div>
    <div id="adm-view-admin-sessions" style="display:none"><div class="adm-loading">${t('admin.loading')}</div></div>
    <div id="adm-view-sessions" style="display:none"><div class="adm-loading">${t('admin.loading')}</div></div>
    <div id="adm-view-updates" style="display:none"></div>
  `;
  root.appendChild(wrap);

  const tabPlayers = wrap.querySelector('#adm-tab-players') as HTMLButtonElement;
  const tabBots = wrap.querySelector('#adm-tab-bots') as HTMLButtonElement;
  const tabAdminSessions = wrap.querySelector('#adm-tab-admin-sessions') as HTMLButtonElement;
  const tabSessions = wrap.querySelector('#adm-tab-sessions') as HTMLButtonElement;
  const tabUpdates = wrap.querySelector('#adm-tab-updates') as HTMLButtonElement;
  const viewPlayers = wrap.querySelector('#adm-view-players') as HTMLElement;
  const viewBots = wrap.querySelector('#adm-view-bots') as HTMLElement;
  const viewAdminSessions = wrap.querySelector('#adm-view-admin-sessions') as HTMLElement;
  const viewSessions = wrap.querySelector('#adm-view-sessions') as HTMLElement;
  const viewUpdates = wrap.querySelector('#adm-view-updates') as HTMLElement;
  const allTabs = [tabPlayers, tabBots, tabAdminSessions, tabSessions, tabUpdates];
  const allViews = [viewPlayers, viewBots, viewAdminSessions, viewSessions, viewUpdates];

  function switchTab(activeTab: HTMLButtonElement, activeView: HTMLElement) {
    allTabs.forEach(t => t.classList.remove('active'));
    allViews.forEach(v => v.style.display = 'none');
    activeTab.classList.add('active');
    activeView.style.display = '';
  }

  tabPlayers.addEventListener('click', () => switchTab(tabPlayers, viewPlayers));
  tabBots.addEventListener('click', () => switchTab(tabBots, viewBots));
  tabAdminSessions.addEventListener('click', () => switchTab(tabAdminSessions, viewAdminSessions));
  tabSessions.addEventListener('click', () => switchTab(tabSessions, viewSessions));
  let updatesInited = false;
  tabUpdates.addEventListener('click', () => {
    switchTab(tabUpdates, viewUpdates);
    if (!updatesInited) { renderUpdatesPanel(viewUpdates); updatesInited = true; }
  });

  // All three tabs share one /api/admin/players fetch — split client-side by isBot.
  loadPlayersAndBots(viewPlayers, viewBots);
  // Admin sessions + regular sessions share one /api/admin/sessions fetch,
  // split client-side by isAdminSession.
  loadSessions(viewAdminSessions, viewSessions);

  return root;
}

// ── Updates Admin Tab ────────────────────────────────────────────────────────

function renderUpdatesPanel(container: HTMLElement): void {
  container.innerHTML = `
    <div class="adm-updates-wrap">
      <div class="adm-updates-section">
        <div class="adm-updates-h">${t('admin.updates.notif_title')}</div>
        <div class="adm-updates-hint">${t('admin.updates.notif_hint')}</div>
        <form id="adm-notif-form" class="adm-updates-form">
          <label>${t('admin.updates.target')}
            <select name="target" id="adm-notif-target">
              <option value="global">${t('admin.updates.target_global')}</option>
              <option value="beta">${t('admin.updates.target_beta')}</option>
              <option value="players">${t('admin.updates.target_players')}</option>
            </select>
          </label>
          <label id="adm-notif-pids-label" style="display:none">${t('admin.updates.player_ids')}
            <input type="text" name="playerIds" id="adm-notif-pids" placeholder="uuid1, uuid2, ..." />
          </label>
          <label>${t('admin.updates.severity')}
            <select name="severity" id="adm-notif-sev">
              <option value="info">info</option>
              <option value="warn">warn</option>
              <option value="crit">crit</option>
            </select>
          </label>
          <label>${t('admin.updates.title_field')}
            <input type="text" name="title" id="adm-notif-title" maxlength="120" required />
          </label>
          <label>${t('admin.updates.body_field')}
            <textarea name="body" id="adm-notif-body" maxlength="2000" rows="4" required></textarea>
          </label>
          <label>${t('admin.updates.link_url')}
            <input type="url" name="linkUrl" id="adm-notif-url" placeholder="https://..." />
          </label>
          <label>${t('admin.updates.link_label')}
            <input type="text" name="linkLabel" id="adm-notif-linklabel" maxlength="40" />
          </label>
          <label>${t('admin.updates.expires_at')}
            <input type="datetime-local" name="expiresAt" id="adm-notif-exp" />
          </label>
          <div class="adm-updates-actions">
            <button type="submit" class="adm-toggle btn sm">${t('admin.updates.send')}</button>
            <span class="adm-updates-status" id="adm-notif-status"></span>
          </div>
        </form>
      </div>

      <div class="adm-updates-section">
        <div class="adm-updates-h">${t('admin.updates.discord_title')}</div>
        <div class="adm-updates-hint">${t('admin.updates.discord_hint')}</div>
        <form id="adm-discord-form" class="adm-updates-form">
          <label>${t('admin.updates.severity')}
            <select name="severity" id="adm-discord-sev">
              <option value="info">info</option>
              <option value="warn">warn</option>
              <option value="crit">crit</option>
            </select>
          </label>
          <label>${t('admin.updates.title_field')}
            <input type="text" name="title" id="adm-discord-title" maxlength="256" required />
          </label>
          <label>${t('admin.updates.body_field')}
            <textarea name="body" id="adm-discord-body" maxlength="4000" rows="4" required></textarea>
          </label>
          <div class="adm-updates-actions">
            <button type="submit" class="adm-toggle btn sm">${t('admin.updates.broadcast')}</button>
            <span class="adm-updates-status" id="adm-discord-status"></span>
          </div>
        </form>
      </div>

      <div class="adm-updates-section">
        <div class="adm-updates-h">${t('admin.updates.motd_title')}</div>
        <div class="adm-updates-hint">${t('admin.updates.motd_hint')}</div>
        <form id="adm-motd-form" class="adm-updates-form">
          <label>${t('admin.updates.severity')}
            <select name="severity" id="adm-motd-sev">
              <option value="info">info</option>
              <option value="warn">warn</option>
              <option value="crit">crit</option>
            </select>
          </label>
          <label>${t('admin.updates.motd_text')}
            <textarea name="text" id="adm-motd-text" maxlength="500" rows="3"></textarea>
          </label>
          <label>${t('admin.updates.expires_at')}
            <input type="datetime-local" name="expiresAt" id="adm-motd-exp" />
          </label>
          <div class="adm-updates-actions">
            <button type="submit" class="adm-toggle btn sm">${t('admin.updates.save')}</button>
            <button type="button" class="adm-delete btn sm red" id="adm-motd-clear">${t('admin.updates.clear')}</button>
            <span class="adm-updates-status" id="adm-motd-status"></span>
          </div>
        </form>
      </div>
    </div>
  `;

  if (!document.getElementById('adm-updates-style')) {
    const s = document.createElement('style');
    s.id = 'adm-updates-style';
    s.textContent = `
      .adm-updates-wrap { display:flex; flex-direction:column; gap:24px; max-width:720px; }
      .adm-updates-section { border:1px solid var(--line); padding:16px; background:var(--surface-frost); backdrop-filter:blur(3px); -webkit-backdrop-filter:blur(3px); }
      .adm-updates-h { font-family:var(--mono); font-size:0.9rem; color:var(--text-hi); letter-spacing:0.12em; margin-bottom:4px; }
      .adm-updates-hint { font-size:0.75rem; color:var(--text-lo); margin-bottom:12px; }
      .adm-updates-form { display:flex; flex-direction:column; gap:10px; }
      .adm-updates-form label { display:flex; flex-direction:column; gap:4px; font-family:var(--mono); font-size:0.72rem; color:var(--text-lo); letter-spacing:0.08em; text-transform:uppercase; }
      .adm-updates-form input, .adm-updates-form textarea, .adm-updates-form select {
        background:var(--bg); border:1px solid var(--border); color:var(--text-hi);
        font-family:var(--mono); font-size:0.85rem; padding:6px 8px; text-transform:none; letter-spacing:0;
      }
      .adm-updates-form input:focus, .adm-updates-form textarea:focus, .adm-updates-form select:focus {
        outline:none; border-color:var(--cyan);
      }
      .adm-updates-actions { display:flex; gap:10px; align-items:center; margin-top:4px; }
      .adm-updates-status { font-family:var(--mono); font-size:0.75rem; color:var(--text-lo); }
      .adm-updates-status.ok { color:var(--green); }
      .adm-updates-status.err { color:var(--red, #e74c3c); }
    `;
    document.head.appendChild(s);
  }

  // Notif form wiring
  const notifTarget = container.querySelector('#adm-notif-target') as HTMLSelectElement;
  const pidsLabel   = container.querySelector('#adm-notif-pids-label') as HTMLElement;
  notifTarget.addEventListener('change', () => {
    pidsLabel.style.display = notifTarget.value === 'players' ? '' : 'none';
  });

  const notifForm = container.querySelector('#adm-notif-form') as HTMLFormElement;
  notifForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const status = container.querySelector('#adm-notif-status') as HTMLElement;
    status.className = 'adm-updates-status';
    status.textContent = t('admin.updates.sending');
    const target   = notifTarget.value as NotificationTarget;
    const severity = (container.querySelector('#adm-notif-sev')      as HTMLSelectElement).value as NotificationSeverity;
    const title    = (container.querySelector('#adm-notif-title')    as HTMLInputElement).value.trim();
    const body     = (container.querySelector('#adm-notif-body')     as HTMLTextAreaElement).value.trim();
    const linkUrl  = (container.querySelector('#adm-notif-url')      as HTMLInputElement).value.trim();
    const linkLabel = (container.querySelector('#adm-notif-linklabel') as HTMLInputElement).value.trim();
    const expRaw   = (container.querySelector('#adm-notif-exp')      as HTMLInputElement).value;
    const pidsRaw  = (container.querySelector('#adm-notif-pids')     as HTMLInputElement).value.trim();
    const playerIds = target === 'players'
      ? pidsRaw.split(',').map(s => s.trim()).filter(Boolean)
      : undefined;

    if (!title || !body) {
      status.className = 'adm-updates-status err';
      status.textContent = t('admin.updates.err_required');
      return;
    }
    try {
      const resp = await adminCreateNotification({
        target, playerIds, severity, title, body,
        linkUrl: linkUrl || undefined,
        linkLabel: linkLabel || undefined,
        expiresAt: expRaw ? new Date(expRaw).toISOString() : undefined,
      });
      status.className = 'adm-updates-status ok';
      status.textContent = t('admin.updates.sent', { n: String(resp.recipients) });
      notifForm.reset();
      pidsLabel.style.display = 'none';
    } catch (err: unknown) {
      status.className = 'adm-updates-status err';
      status.textContent = (err instanceof ApiError) ? err.message : String(err);
    }
  });

  // Discord form
  const discordForm = container.querySelector('#adm-discord-form') as HTMLFormElement;
  discordForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const status = container.querySelector('#adm-discord-status') as HTMLElement;
    status.className = 'adm-updates-status';
    status.textContent = t('admin.updates.sending');
    const severity = (container.querySelector('#adm-discord-sev')   as HTMLSelectElement).value as NotificationSeverity;
    const title    = (container.querySelector('#adm-discord-title') as HTMLInputElement).value.trim();
    const body     = (container.querySelector('#adm-discord-body')  as HTMLTextAreaElement).value.trim();
    if (!title || !body) {
      status.className = 'adm-updates-status err';
      status.textContent = t('admin.updates.err_required');
      return;
    }
    try {
      await adminBroadcastDiscord({ title, body, severity });
      status.className = 'adm-updates-status ok';
      status.textContent = t('admin.updates.broadcast_ok');
      discordForm.reset();
    } catch (err: unknown) {
      status.className = 'adm-updates-status err';
      status.textContent = (err instanceof ApiError) ? err.message : String(err);
    }
  });

  // Motd form — load current value
  const motdForm   = container.querySelector('#adm-motd-form') as HTMLFormElement;
  const motdText   = container.querySelector('#adm-motd-text') as HTMLTextAreaElement;
  const motdSev    = container.querySelector('#adm-motd-sev')  as HTMLSelectElement;
  const motdExp    = container.querySelector('#adm-motd-exp')  as HTMLInputElement;
  const motdStatus = container.querySelector('#adm-motd-status') as HTMLElement;
  const motdClear  = container.querySelector('#adm-motd-clear')  as HTMLButtonElement;

  getMotd().then(m => {
    if (m.text) motdText.value = m.text;
    if (m.severity) motdSev.value = m.severity;
    if (m.expiresAt) {
      const d = new Date(m.expiresAt);
      if (Number.isFinite(d.getTime())) {
        motdExp.value = d.toISOString().slice(0, 16);
      }
    }
  }).catch(() => { /* ignore */ });

  motdForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    motdStatus.className = 'adm-updates-status';
    motdStatus.textContent = t('admin.updates.sending');
    try {
      await adminPutMotd({
        text: motdText.value,
        severity: (motdSev.value as NotificationSeverity),
        expiresAt: motdExp.value ? new Date(motdExp.value).toISOString() : undefined,
      });
      motdStatus.className = 'adm-updates-status ok';
      motdStatus.textContent = t('admin.updates.saved');
    } catch (err: unknown) {
      motdStatus.className = 'adm-updates-status err';
      motdStatus.textContent = (err instanceof ApiError) ? err.message : String(err);
    }
  });

  motdClear.addEventListener('click', async () => {
    motdStatus.className = 'adm-updates-status';
    motdStatus.textContent = t('admin.updates.sending');
    try {
      await adminPutMotd({ text: '', severity: 'info' });
      motdText.value = '';
      motdExp.value = '';
      motdStatus.className = 'adm-updates-status ok';
      motdStatus.textContent = t('admin.updates.cleared');
    } catch (err: unknown) {
      motdStatus.className = 'adm-updates-status err';
      motdStatus.textContent = (err instanceof ApiError) ? err.message : String(err);
    }
  });
}

// ── Relative time helper ──────────────────────────────────────────────────────

function timeAgo(iso: string | undefined): string {
  if (!iso) return t('admin.never');
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return t('admin.never');
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return '<1m';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

// ── Players + Bots Tabs (one fetch, split by isBot) ───────────────────────────

async function loadPlayersAndBots(playersContainer: HTMLElement, botsContainer: HTMLElement): Promise<void> {
  try {
    const [allPlayers, onlineMap] = await Promise.all([getAdminPlayers(), getAdminOnline()]);
    const jwt = getJwt();
    const me = jwt ? parseJwt(jwt) : null;
    if (me?.sub && !onlineMap[me.sub]) onlineMap[me.sub] = 'hub';

    const humans = allPlayers.filter(p => !p.isBot);
    const bots = allPlayers.filter(p => p.isBot === true);

    renderPlayersTable(playersContainer, humans, onlineMap);
    renderBotsTable(botsContainer, bots);

    // Poll online status every 10s — affects only the humans table.
    const interval = setInterval(async () => {
      if (!document.contains(playersContainer)) { clearInterval(interval); return; }
      try {
        const fresh = await getAdminOnline();
        if (me?.sub && !fresh[me.sub]) fresh[me.sub] = 'hub';
        updateOnlineDots(playersContainer, fresh);
      } catch { /* silent — polling failure is not critical */ }
    }, 10_000);
  } catch (e) {
    const msg = `<div class="adm-loading" style="color:var(--red)">${(e as Error).message}</div>`;
    playersContainer.innerHTML = msg;
    botsContainer.innerHTML = msg;
  }
}

function updateOnlineDots(container: HTMLElement, onlineMap: Record<string, string>): void {
  container.querySelectorAll<HTMLElement>('.adm-dot').forEach(dot => {
    const pid = dot.dataset.playerId;
    if (!pid) return;
    const sessionId = onlineMap[pid];
    const isOnline = !!sessionId;
    dot.className = `adm-dot ${isOnline ? 'online' : 'offline'}`;
    const label = dot.nextElementSibling;
    if (label?.classList.contains('adm-online-session')) {
      label.textContent = sessionId ? `${t('admin.in_session')} ${sessionId.substring(0, 8)}` : '';
    }
  });
}

function renderPlayersTable(container: HTMLElement, players: AdminPlayer[], onlineMap: Record<string, string>): void {
  const colCount = 11;
  container.innerHTML = `
    <table class="adm-table dtable">
      <thead><tr>
        <th>${t('admin.col_name')}</th>
        <th>${t('admin.col_email')}</th>
        <th>${t('admin.col_beta')}</th>
        <th>${t('admin.col_admin')}</th>
        <th>${t('admin.col_active_session')}</th>
        <th>${t('admin.col_last_login')}</th>
        <th>${t('admin.col_last_activity')}</th>
        <th>${t('admin.col_updated')}</th>
        <th>${t('admin.col_created')}</th>
        <th></th>
        <th>${t('admin.col_delete')}</th>
      </tr></thead>
      <tbody id="adm-players-body"></tbody>
    </table>
  `;
  const tbody = container.querySelector('#adm-players-body') as HTMLElement;

  for (const p of players) {
    const created = p.createdAt.substring(0, 10);
    const updated = p.updatedAt.substring(0, 10);
    const isOnline = !!onlineMap[p.id];
    const sessionId = onlineMap[p.id] ?? '';

    const tr = document.createElement('tr');
    tr.className = 'adm-player-row';
    tr.dataset.playerId = p.id;

    const avatarHtml = p.avatarUrl
      ? `<img class="adm-avatar" src="${esc(p.avatarUrl)}" alt="" loading="lazy">`
      : `<span class="adm-avatar-fallback">${esc(p.name.charAt(0).toUpperCase())}</span>`;

    // Active session cell — mirrors renderBotsTable rendering so Players
    // and Bots tabs stay symmetric. Empty when the human isn't currently in
    // any lobby/running session.
    let activeCell = '';
    if (p.activeSessionId && p.activeSessionName) {
      const statusCls = p.activeSessionStatus === 'running' ? 'running' : 'lobby';
      activeCell = `<span class="adm-status chip ${statusCls}">${esc(p.activeSessionStatus ?? '').toUpperCase()}</span> <span class="adm-detail-name">${esc(p.activeSessionName)}</span>`;
    }

    tr.innerHTML = `
      <td>
        <span class="adm-chevron" data-chevron>▸</span>
        <span class="adm-dot ${isOnline ? 'online' : 'offline'}" data-player-id="${esc(p.id)}"></span>
        <span class="adm-online-session">${sessionId ? `${t('admin.in_session')} ${sessionId.substring(0, 8)}` : ''}</span>
        ${avatarHtml}${esc(p.name)}
      </td>
      <td><span class="adm-email">${esc(p.email)}</span></td>
      <td></td>
      <td>${p.isAdmin ? `<span class="adm-badge admin chip">ADMIN</span>` : ''}</td>
      <td>${activeCell}</td>
      <td><span class="adm-login-time">${timeAgo(p.lastLoginAt)}</span></td>
      <td><span class="adm-login-time">${timeAgo(p.lastActivityAt)}</span></td>
      <td>${updated}</td>
      <td>${created}</td>
      <td></td>
      <td></td>
    `;

    // Beta toggle
    const betaCell = tr.children[2] as HTMLElement;
    const betaBtn = document.createElement('button');
    betaBtn.className = `adm-toggle btn sm${p.isBetaUser ? ' on' : ''}`;
    betaBtn.textContent = p.isBetaUser ? t('admin.beta_on') : t('admin.beta_off');
    betaBtn.addEventListener('click', async (e) => {
      e.stopPropagation();
      betaBtn.disabled = true;
      const newVal = !p.isBetaUser;
      try {
        await patchAdminPlayer(p.id, { isBetaUser: newVal });
        p.isBetaUser = newVal;
        betaBtn.className = `adm-toggle btn sm${newVal ? ' on' : ''}`;
        betaBtn.textContent = newVal ? t('admin.beta_on') : t('admin.beta_off');
      } catch {
        betaBtn.textContent = t('admin.toggle_error');
        setTimeout(() => { betaBtn.textContent = p.isBetaUser ? t('admin.beta_on') : t('admin.beta_off'); }, 2000);
      }
      betaBtn.disabled = false;
    });
    betaCell.appendChild(betaBtn);

    // Delete button (col 10 / index 10 after v1.8.4 added col_active_session)
    const delCell = tr.children[10] as HTMLElement;
    delCell.appendChild(buildDeleteButton(p, () => tr.remove()));

    // Row click → expand/collapse session detail
    let expanded = false;
    let detailRow: HTMLTableRowElement | null = null;

    tr.addEventListener('click', async () => {
      const chevron = tr.querySelector('[data-chevron]') as HTMLElement;
      if (expanded && detailRow) {
        detailRow.remove();
        detailRow = null;
        expanded = false;
        tr.classList.remove('expanded');
        chevron.classList.remove('open');
        return;
      }
      expanded = true;
      tr.classList.add('expanded');
      chevron.classList.add('open');

      detailRow = document.createElement('tr');
      detailRow.className = 'adm-detail-row';
      detailRow.innerHTML = `<td colspan="${colCount}"><div class="adm-detail-loading">${t('admin.loading')}</div></td>`;
      tr.after(detailRow);

      try {
        const sessions = await getAdminPlayerSessions(p.id);
        if (!detailRow) return;
        const cell = detailRow.querySelector('td') as HTMLElement;
        if (sessions.length === 0) {
          cell.innerHTML = `<div class="adm-detail-empty">${t('admin.no_sessions')}</div>`;
        } else {
          cell.innerHTML = sessions.map(s => `
            <div class="adm-detail-session">
              <span class="adm-detail-color" style="background:${esc(s.accentColor)}"></span>
              <span class="adm-detail-corp">${esc(s.corpName)}</span>
              <span class="adm-detail-name">${esc(s.sessionName)}</span>
              <span class="adm-status chip ${s.status}">${s.status.toUpperCase()}</span>
              <span style="color:var(--text-dim)">Day ${s.day}</span>
            </div>
          `).join('');
        }
      } catch (err) {
        if (detailRow) {
          const cell = detailRow.querySelector('td') as HTMLElement;
          cell.innerHTML = `<div class="adm-detail-empty" style="color:var(--red)">${(err as Error).message}</div>`;
        }
      }
    });

    tbody.appendChild(tr);
  }

  if (players.length === 0) {
    tbody.innerHTML = `<tr><td colspan="${colCount}" class="adm-detail-empty">${t('admin.no_players')}</td></tr>`;
  }
}

function renderBotsTable(container: HTMLElement, bots: AdminPlayer[]): void {
  const colCount = 6;
  container.innerHTML = `
    <table class="adm-table dtable">
      <thead><tr>
        <th>${t('admin.col_name')}</th>
        <th>${t('admin.col_email')}</th>
        <th>${t('admin.col_active_session')}</th>
        <th>${t('admin.col_last_activity')}</th>
        <th>${t('admin.col_created')}</th>
        <th>${t('admin.col_delete')}</th>
      </tr></thead>
      <tbody id="adm-bots-body"></tbody>
    </table>
  `;
  const tbody = container.querySelector('#adm-bots-body') as HTMLElement;

  for (const b of bots) {
    const created = b.createdAt.substring(0, 10);
    const tr = document.createElement('tr');
    tr.dataset.playerId = b.id;

    const avatarHtml = b.avatarUrl
      ? `<img class="adm-avatar" src="${esc(b.avatarUrl)}" alt="" loading="lazy">`
      : `<span class="adm-avatar-fallback">${esc(b.name.charAt(0).toUpperCase())}</span>`;

    // Active session cell — when the bot is currently in lobby or running,
    // render a chip with the session name + status. Empty cell otherwise.
    let activeCell = '';
    if (b.activeSessionId && b.activeSessionName) {
      const statusCls = b.activeSessionStatus === 'running' ? 'running' : 'lobby';
      activeCell = `<span class="adm-status chip ${statusCls}">${esc(b.activeSessionStatus ?? '').toUpperCase()}</span> <span class="adm-detail-name">${esc(b.activeSessionName)}</span>`;
    }

    tr.innerHTML = `
      <td>${avatarHtml}${esc(b.name)} <span class="adm-badge bot chip">BOT</span></td>
      <td><span class="adm-email">${esc(b.email)}</span></td>
      <td>${activeCell}</td>
      <td><span class="adm-login-time">${timeAgo(b.lastActivityAt)}</span></td>
      <td>${created}</td>
      <td></td>
    `;

    const delCell = tr.children[5] as HTMLElement;
    delCell.appendChild(buildDeleteButton(b, () => tr.remove()));

    tbody.appendChild(tr);
  }

  if (bots.length === 0) {
    tbody.innerHTML = `<tr><td colspan="${colCount}" class="adm-detail-empty">${t('admin.no_bots')}</td></tr>`;
  }
}

function buildDeleteButton(p: AdminPlayer, onRemoved: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = 'adm-delete btn sm red';
  btn.textContent = t('admin.delete');
  btn.addEventListener('click', async (e) => {
    e.stopPropagation();
    const confirmMsg = t('admin.delete_confirm', { name: p.name });
    // eslint-disable-next-line no-alert
    if (!confirm(confirmMsg)) return;
    btn.disabled = true;
    const oldText = btn.textContent;
    btn.textContent = t('admin.deleting');
    try {
      await deleteAdminPlayer(p.id);
      onRemoved();
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status}` : 'ERR';
      btn.textContent = `${t('admin.delete_error')} (${msg})`;
      setTimeout(() => {
        btn.textContent = oldText;
        btn.disabled = false;
      }, 2500);
    }
  });
  return btn;
}

// ── Sessions + Admin Sessions Tabs ────────────────────────────────────────────

async function loadSessions(adminContainer: HTMLElement, regularContainer: HTMLElement): Promise<void> {
  try {
    const sessions = await getAdminSessions();
    const adminOnes = sessions.filter(s => s.isAdminSession === true);
    const regular = sessions.filter(s => !s.isAdminSession);
    renderAdminSessionsTable(adminContainer, adminOnes);
    renderSessionsTable(regularContainer, regular);
  } catch (e) {
    const msg = `<div class="adm-loading" style="color:var(--red)">${(e as Error).message}</div>`;
    adminContainer.innerHTML = msg;
    regularContainer.innerHTML = msg;
  }
}

/** v1.9.0 (specs#32): Admin Sessions tab — dedicated test-harness list.
 *  Create-button at top opens a minimal create form. Per-row: resolve-tick
 *  + delete. Sessions here are filtered out of all stats aggregators and
 *  skipped by the deadline scheduler, so they're safe for feature-testing. */
function renderAdminSessionsTable(container: HTMLElement, sessions: AdminSession[]): void {
  container.innerHTML = `
    <div style="margin-bottom:16px">
      <button id="adm-create-admin-session" class="adm-resolve btn sm amber">${t('admin.create_admin_session')}</button>
      <span style="margin-left:12px;color:var(--text-lo);font-size:0.72rem">${t('admin.admin_session_hint')}</span>
    </div>
    <table class="adm-table dtable">
      <thead><tr>
        <th>${t('admin.col_name')}</th>
        <th>${t('admin.col_status')}</th>
        <th>${t('admin.col_players')}</th>
        <th>${t('admin.col_day')}</th>
        <th>${t('admin.col_mode')}</th>
        <th>${t('admin.col_created')}</th>
        <th>${t('admin.col_actions')}</th>
      </tr></thead>
      <tbody></tbody>
    </table>
  `;

  const createBtn = container.querySelector('#adm-create-admin-session') as HTMLButtonElement;
  createBtn.addEventListener('click', async () => {
    const name = prompt(t('admin.create_admin_session_name_prompt'));
    if (!name) return;
    const corp = prompt(t('admin.create_admin_session_corp_prompt')) || 'Admin Corp';
    createBtn.disabled = true;
    createBtn.textContent = t('admin.creating');
    try {
      await createAdminSession({
        name, corpName: corp, mode: 'standard', maxPlayers: 8, deadlineHour: 22,
      });
      // Reload list
      const fresh = await getAdminSessions();
      renderAdminSessionsTable(container, fresh.filter(s => s.isAdminSession === true));
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status}: ${err.body}` : String(err);
      createBtn.textContent = `${t('admin.create_error')}: ${msg}`;
      setTimeout(() => {
        createBtn.disabled = false;
        createBtn.textContent = t('admin.create_admin_session');
      }, 3000);
    }
  });

  const tbody = container.querySelector('tbody') as HTMLElement;
  if (sessions.length === 0) {
    tbody.innerHTML = `<tr><td colspan="7" class="adm-detail-empty">${t('admin.no_admin_sessions')}</td></tr>`;
    return;
  }
  for (const s of sessions) {
    const tr = document.createElement('tr');
    const created = s.createdAt.substring(0, 10);
    tr.innerHTML = `
      <td>${esc(s.name)}</td>
      <td><span class="adm-status chip ${s.status}">${s.status.toUpperCase()}</span></td>
      <td>${s.playerCount}/${s.maxPlayers}</td>
      <td>${s.day}</td>
      <td>${s.mode}</td>
      <td>${created}</td>
      <td></td>
    `;
    const actionCell = tr.lastElementChild as HTMLElement;
    // Resolve-tick — always shown on admin sessions (lobby or running) so
    // admins can advance the session manually regardless of the deadline.
    const tickBtn = document.createElement('button');
    tickBtn.className = 'adm-resolve btn sm amber';
    tickBtn.textContent = t('admin.resolve_tick_now');
    tickBtn.style.marginRight = '6px';
    tickBtn.addEventListener('click', async () => {
      tickBtn.disabled = true;
      tickBtn.textContent = t('admin.resolving');
      try {
        const res = await resolveTickAndPoll(s.id, (phase, pct) => {
          if (phase) tickBtn.textContent = `${phase}… ${pct}%`;
        });
        const dayCell = tr.children[3] as HTMLElement;
        dayCell.textContent = String(res.day);
      } catch (err) {
        const msg = err instanceof ApiError ? `${err.status}: ${err.body}` : String(err);
        tickBtn.textContent = msg;
      } finally {
        setTimeout(() => {
          tickBtn.disabled = false;
          tickBtn.textContent = t('admin.resolve_tick_now');
        }, 1000);
      }
    });
    actionCell.appendChild(tickBtn);

    // Spawn-bot — v1.9.1 admin-only mid-game bot injection. Server rejects
    // this on non-admin sessions with 403. Prompts for corp + strategy;
    // corp defaults to a random unused one if the prompt is left blank.
    const spawnBtn = document.createElement('button');
    spawnBtn.className = 'adm-resolve btn sm amber';
    spawnBtn.textContent = t('admin.spawn_bot');
    spawnBtn.style.marginRight = '6px';
    spawnBtn.addEventListener('click', async () => {
      const corp = prompt(t('admin.spawn_bot_corp_prompt')) || '';
      if (!corp.trim()) return;
      const strategy = prompt(t('admin.spawn_bot_strategy_prompt'), 'diplomat') || 'diplomat';
      spawnBtn.disabled = true;
      spawnBtn.textContent = t('admin.spawning');
      try {
        const res = await spawnBotsAdmin(s.id, {
          bots: [{ corpName: corp.trim(), strategy: strategy.trim(), temperament: 'balanced', difficulty: 'normal' }],
        });
        spawnBtn.textContent = `+${res.spawned.length}`;
        setTimeout(() => {
          spawnBtn.disabled = false;
          spawnBtn.textContent = t('admin.spawn_bot');
        }, 2000);
      } catch (err) {
        const msg = err instanceof ApiError ? `${err.status}` : 'ERR';
        spawnBtn.textContent = `${t('admin.spawn_bot_error')} (${msg})`;
        setTimeout(() => {
          spawnBtn.disabled = false;
          spawnBtn.textContent = t('admin.spawn_bot');
        }, 3000);
      }
    });
    actionCell.appendChild(spawnBtn);

    // Delete — admin sessions are disposable, no confirmation chain.
    const delBtn = document.createElement('button');
    delBtn.className = 'adm-delete btn sm red';
    delBtn.textContent = t('admin.delete');
    delBtn.addEventListener('click', async () => {
      if (!confirm(t('admin.delete_admin_session_confirm', { name: s.name }))) return;
      delBtn.disabled = true;
      try {
        await deleteAdminSession(s.id);
        tr.remove();
      } catch (err) {
        const msg = err instanceof ApiError ? `${err.status}` : 'ERR';
        delBtn.textContent = `${t('admin.delete_error')} (${msg})`;
        setTimeout(() => {
          delBtn.textContent = t('admin.delete');
          delBtn.disabled = false;
        }, 2500);
      }
    });
    actionCell.appendChild(delBtn);

    tbody.appendChild(tr);
  }
}

function renderSessionsTable(container: HTMLElement, sessions: AdminSession[]): void {
  container.innerHTML = `
    <table class="adm-table dtable">
      <thead><tr>
        <th>${t('admin.col_name')}</th>
        <th>${t('admin.col_status')}</th>
        <th>${t('admin.col_players')}</th>
        <th>${t('admin.col_day')}</th>
        <th>${t('admin.col_mode')}</th>
        <th>${t('admin.col_created')}</th>
        <th>${t('admin.col_last_tick')}</th>
        <th>${t('admin.col_winner')}</th>
        <th>${t('admin.col_actions')}</th>
      </tr></thead>
      <tbody></tbody>
    </table>
  `;
  const tbody = container.querySelector('tbody') as HTMLElement;
  for (const s of sessions) {
    const tr = document.createElement('tr');
    const created = s.createdAt.substring(0, 10);
    const lastTick = s.lastTickAt ? s.lastTickAt.substring(0, 16).replace('T', ' ') : '—';
    tr.innerHTML = `
      <td>${esc(s.name)}</td>
      <td><span class="adm-status chip ${s.status}">${s.status.toUpperCase()}</span></td>
      <td>${s.playerCount}/${s.maxPlayers}</td>
      <td>${s.day}</td>
      <td>${s.mode}</td>
      <td>${created}</td>
      <td>${lastTick}</td>
      <td>${s.winner ? esc(s.winner) : '—'}</td>
      <td></td>
    `;
    const actionCell = tr.lastElementChild as HTMLElement;
    if (s.status === 'running') {
      const btn = document.createElement('button');
      btn.className = 'adm-resolve btn sm amber';
      btn.textContent = t('admin.resolve_tick');
      const statusEl = document.createElement('div');
      statusEl.className = 'adm-resolve-status';
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        btn.textContent = t('admin.resolving');
        statusEl.textContent = '';
        try {
          const res = await resolveTickAndPoll(s.id, (phase, pct) => {
            if (phase) statusEl.textContent = `${phase}… ${pct}%`;
          });
          statusEl.style.color = 'var(--green)';
          statusEl.textContent = `Day ${res.day}${res.gameOver ? ' — GAME OVER' : ''}`;
          const dayCell = tr.children[3] as HTMLElement;
          dayCell.textContent = String(res.day);
        } catch (err) {
          const msg = err instanceof ApiError ? `${err.status}: ${err.body}` : String(err);
          statusEl.style.color = 'var(--red)';
          statusEl.textContent = msg;
        } finally {
          btn.disabled = false;
          btn.textContent = t('admin.resolve_tick');
        }
      });
      actionCell.appendChild(btn);
      actionCell.appendChild(statusEl);
    }
    tbody.appendChild(tr);
  }
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
